import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { useAuth } from "src/contexts/AuthContext";
import { loginSchema } from "src/schemas/auth";
import { Checkbox } from "src/components/ui/checkbox";
import { Logo } from "src/components/Logo";
import { cn } from "src/utils/cn";

/* ─────────────────────────────────────────────
   Shared primitives
───────────────────────────────────────────── */

function FieldLabel({ htmlFor, children }) {
  return (
    <label
      htmlFor={htmlFor}
      className="mb-1.5 block text-[13px] font-medium text-slate-600"
    >
      {children}
    </label>
  );
}

function FieldInput({ className, ...props }) {
  return (
    <input
      className={cn(
        "h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-[14px] text-slate-800 placeholder:text-slate-400 transition-colors focus:border-[#4b5bed] focus:outline-none focus:ring-2 focus:ring-[#4b5bed]/20 disabled:opacity-50",
        className
      )}
      {...props}
    />
  );
}

function InlineError({ message }) {
  if (!message) return null;
  return <p className="mt-1 text-[12px] text-rose-500">{message}</p>;
}

function Flash({ message, variant = "error" }) {
  if (!message) return null;
  const styles =
    variant === "success"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : "border-rose-200 bg-rose-50 text-rose-700";
  return (
    <p className={`rounded-md border px-3 py-2 text-[13px] ${styles}`}>
      {message}
    </p>
  );
}

/* ─────────────────────────────────────────────
   Left branding panel (dark, full-height)
───────────────────────────────────────────── */

function CoverPanel() {
  return (
    <div className="auth-card-cover">
      <svg
        className="absolute inset-0 h-full w-full"
        viewBox="0 0 800 900"
        preserveAspectRatio="xMidYMid slice"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="bgGrad" x1="0" y1="0" x2="0.3" y2="1">
            <stop offset="0%" stopColor="#0f1629" />
            <stop offset="50%" stopColor="#1a2347" />
            <stop offset="100%" stopColor="#0d1220" />
          </linearGradient>
          <radialGradient id="glowCenter" cx="50%" cy="70%" r="50%">
            <stop offset="0%" stopColor="#4b6ef5" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#4b6ef5" stopOpacity="0" />
          </radialGradient>
          <filter id="blur6">
            <feGaussianBlur stdDeviation="6" />
          </filter>
          <filter id="blur3">
            <feGaussianBlur stdDeviation="3" />
          </filter>
        </defs>

        <rect width="800" height="900" fill="url(#bgGrad)" />
        <rect width="800" height="900" fill="url(#glowCenter)" />

        {/* Vertical light rays */}
        {[120, 200, 280, 340, 400, 460, 520, 580, 660].map((x, i) => (
          <line
            key={x}
            x1={x}
            y1="0"
            x2={x + (i % 2 === 0 ? 5 : -5)}
            y2={350 + (i % 3) * 80}
            stroke={i % 3 === 0 ? "#6c8cff" : i % 3 === 1 ? "#22d3bb" : "#a78bfa"}
            strokeWidth={i % 2 === 0 ? 1.5 : 1}
            strokeOpacity={0.3 + (i % 4) * 0.1}
          />
        ))}

        {/* Thin accent rays */}
        {[150, 240, 310, 430, 500, 610, 700].map((x, i) => (
          <line
            key={`t${x}`}
            x1={x}
            y1="0"
            x2={x}
            y2={200 + (i % 3) * 100}
            stroke={i % 2 === 0 ? "#f472b6" : "#34d399"}
            strokeWidth="0.5"
            strokeOpacity="0.25"
          />
        ))}

        {/* Floating particles */}
        {[
          [180, 160], [350, 120], [520, 200], [650, 100], [100, 280],
          [420, 250], [600, 300], [250, 80], [700, 220], [300, 320],
        ].map(([cx, cy], i) => (
          <circle
            key={`p${i}`}
            cx={cx}
            cy={cy}
            r={i % 3 === 0 ? 2 : 1.2}
            fill={i % 3 === 0 ? "#6c8cff" : i % 3 === 1 ? "#f472b6" : "#34d399"}
            opacity={0.4 + (i % 3) * 0.15}
          />
        ))}

        {/* Concentric rings at bottom */}
        {[100, 140, 185, 235, 290].map((r, i) => (
          <ellipse
            key={`r${i}`}
            cx="400"
            cy="820"
            rx={r * 1.6}
            ry={r * 0.4}
            fill="none"
            stroke="#4b6ef5"
            strokeWidth={i === 0 ? 1.5 : 0.8}
            strokeOpacity={0.25 - i * 0.03}
          />
        ))}

        {/* Glow on rings */}
        <ellipse cx="400" cy="820" rx="160" ry="40" fill="#4b6ef5" fillOpacity="0.08" filter="url(#blur6)" />
        <ellipse cx="400" cy="820" rx="80" ry="20" fill="#22d3bb" fillOpacity="0.06" filter="url(#blur3)" />
      </svg>

      {/* Branding content */}
      <div className="relative z-10 flex flex-col items-center text-center px-12">
        <Logo variant="white" size="lg" />
        <p className="mt-8 text-[22px] font-bold leading-snug text-white/90 sm:text-[28px]">
          Where leads move, convert,<br /> and Close
        </p>
        <p className="mt-4 text-[14px] text-white/50 max-w-[360px]">
          The all-in-one CRM built for Indian real estate developers and brokers.
        </p>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Auth card shell
───────────────────────────────────────────── */

function AuthCard({ children }) {
  return (
    <div className="auth-shell">
      <div className="auth-card">
        <CoverPanel />
        <div className="auth-card-form">
          <div className="auth-card-form-inner">{children}</div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Signup page
───────────────────────────────────────────── */

function SignupPage() {
  return (
    <AuthCard>
      <div className="text-center py-4">
        <h1 className="text-[22px] font-bold tracking-tight text-[#1a2347] mb-2">
          Registration is Closed
        </h1>
        <p className="text-[14px] text-slate-500 mb-6 leading-relaxed">
          Self-registration is currently disabled. To create an organization or request access, please book a live demo with our team.
        </p>
        <a
          href="/#contact"
          className="inline-flex w-full items-center justify-center rounded-full bg-[#2d4fd6] py-3 text-sm font-semibold text-white shadow-[0_4px_14px_rgba(45,79,214,0.35)] transition-all hover:bg-[#2440c0]"
        >
          Book a Demo
        </a>
        <p className="mt-6 text-[13px] text-slate-500">
          Already have an account?{" "}
          <Link to="/login" className="font-semibold text-[#2d4fd6] hover:underline">
            Login
          </Link>
        </p>
      </div>
    </AuthCard>
  );
}

/* ─────────────────────────────────────────────
   Login page
───────────────────────────────────────────── */

function LoginPage() {
  const { login } = useAuth();
  const [rememberMe, setRememberMe] = useState(false);
  const [serverError, setServerError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(loginSchema),
    mode: "onBlur",
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = async (values) => {
    setServerError("");
    setIsSubmitting(true);
    try {
      await login({ ...values, rememberMe });
    } catch (error) {
      // error can be a plain string (Devise failure) or an envelope object
      // ({ code, message }, e.g. ORGANIZATION_DEACTIVATED).
      const data = error.response?.data;
      const message =
        data?.error?.message ||
        (typeof data?.error === "string" ? data.error : null) ||
        data?.errors?.join(", ") ||
        "Invalid email or password.";
      setServerError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthCard>
      <div className="mb-6 text-center">
        <h1 className="text-[22px] font-bold tracking-tight text-[#1a2347]">
          Welcome Back
        </h1>
        <p className="mt-1 text-[13px] text-slate-500">Sign in to your account</p>
      </div>

      <form className="auth-form-stack" noValidate onSubmit={handleSubmit(onSubmit)}>
        <Flash message={serverError} />

        {/* Email */}
        <div>
          <FieldLabel htmlFor="user_email">Email</FieldLabel>
          <FieldInput
            id="user_email"
            type="email"
            autoComplete="email"
            placeholder="email@company.com"
            {...register("email")}
          />
          <InlineError message={errors.email?.message} />
        </div>

        {/* Password */}
        <div>
          <FieldLabel htmlFor="user_password">Password</FieldLabel>
          <FieldInput
            id="user_password"
            type="password"
            autoComplete="current-password"
            placeholder="••••••••"
            {...register("password")}
          />
          <InlineError message={errors.password?.message} />
        </div>

        {/* Remember me */}
        <label className="flex items-center gap-2 text-[13px] text-slate-600" htmlFor="user_remember_me">
          <Checkbox
            id="user_remember_me"
            className="border-slate-300 data-[state=checked]:bg-[#2d4fd6] data-[state=checked]:border-[#2d4fd6]"
            checked={rememberMe}
            onCheckedChange={(v) => setRememberMe(v === true)}
          />
          Keep me signed in
        </label>

        {/* Submit */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="mt-2 h-11 w-full rounded-full bg-[#2d4fd6] text-[14px] font-bold text-white shadow-[0_4px_14px_rgba(45,79,214,0.35)] transition-all duration-200 hover:bg-[#2440c0] hover:shadow-[0_6px_20px_rgba(45,79,214,0.45)] active:scale-[0.98] disabled:opacity-60"
        >
          {isSubmitting ? "Signing in…" : "Sign In"}
        </button>
      </form>
    </AuthCard>
  );
}

export { LoginPage, SignupPage };
